import {
  BackgroundColor,
  CardBackgroundColor,
  ContentAnchor,
  getBackgroundColor,
} from '@api/selections';
import { BlockComponentBaseProps, EpiImage, EpiLink, ImageConfig } from '@api/types';
import { BackgroundCard, EditPanel, Html, SmartLink, Title } from '@components';
import { Box, Container, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints, palette } from '@themes/theme.helpers';
import { getEpiImageResizerUrl } from '@utils/images.utils';
import classnames from 'classnames';

// STYLES
//--------
const BannerBlockWrapper = styled(Box)`
  // Color variants
  // Background colors
  &.bg-black {
    background: ${palette.common('black')};
  }

  &.bg-white {
    background: ${palette.common('white')};
  }

  &.bg-transparent {
    background: transparent;
  }

  &.center .block-container {
    justify-content: center;
    align-items: center;
  }

  &.left .block-container {
    justify-content: start;
    align-items: center;
  }

  &.right .block-container {
    justify-content: end;
    align-items: center;
  }

  &.top .block-container {
    justify-content: center;
    align-items: start;
  }

  &.bottom .block-container {
    justify-content: center;
    align-items: end;
  }

  &.bottom.center .block-container {
    justify-content: center;
    align-items: end;
  }

  &.bottom.left .block-container {
    justify-content: start;
    align-items: end;
  }

  &.bottom.right .block-container {
    justify-content: right;
    align-items: end;
  }

  &.top.left .block-container {
    justify-content: start;
    align-items: start;
  }

  &.top.center .block-container {
    justify-content: center;
    align-items: start;
  }

  &.top.right .block-container {
    justify-content: end;
    align-items: start;
  }
  padding: 32px 0;
  // Main content wrapper
  .banner-content {
    display: flex;
    align-items: center;
    flex-direction: column;
    width: 100%;
    height: 512px;
    position: relative;
  }

  .block-container {
    display: flex;
    position: absolute;
    height: 100%;
  }

  .banner-card {
    padding: 40px 36px;
    z-index: 100;
    flex-grow: 1;
    border-radius: 4px;
    color: ${palette.text('primary')};
    position: relative;
    max-width: 520px;
    gap: 10px;
    max-height: 100%;
    overflow: hidden;

    // Hover effect
    transition: transform 200ms linear;

    ${breakpoints.up('md')} {
      display: flex;
      flex-direction: column;
      justify-content: start;
    }

    // With Link
    &.with-link:hover {
      transform: scale(1.05);
    }

    p {
      margin-bottom: 0;
    }

    &.quote {
      background: ${colors.secondary.lightBlue};
      &.left {
        padding-bottom: 32px;
        ${breakpoints.up('md')} {
          padding-bottom: 64px;
        }
      }
      &.right {
        padding-top: 32px;
        ${breakpoints.up('md')} {
          padding-top: 64px;
        }
      }
      .description * {
        font-family: 'Lato';
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        font-size: 16px;
        text-align: left;
        ${breakpoints.up('md')} {
          font-size: 24px;
        }
      }
    }
  }
`;

// TYPES
//-------
export type BannerBlockProps = BlockComponentBaseProps & {
  title?: string;
  text?: string;
  image?: EpiImage;
  link?: EpiLink;
  backgroundColor?: BackgroundColor;
  cardBackgroundColor?: CardBackgroundColor;
  contentPosition?: ContentAnchor;
  imageConfig?: ImageConfig;
  hideOnMobile?: boolean;
};

export const BannerBlock = ({
  title,
  image,
  text,
  link,
  backgroundColor = 'transparent',
  cardBackgroundColor = 'transparent',
  disableEditPanel = false,
  contentPosition = 'bottom center',
  imageConfig,
  hideOnMobile,
}: BannerBlockProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const backgroundColorValue = backgroundColor ? backgroundColor : 'transparent';
  const mainClass = classnames(
    'banner-block',
    contentPosition,
    getBackgroundColor(backgroundColorValue)
  );
  const cardClass = classnames('banner-card', {
    'with-link': link,
  });

  const hasContent = title || text;
  const imageUrl = image ? getEpiImageResizerUrl(image) : '';
  const mainContent = (
    <Box
      className='banner-content'
      sx={{
        background: `url(${imageUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: imageConfig?.anchor || 'center',
        backgroundSize:
          imageConfig?.scalingMode === 'fit' ? 'cover' : imageConfig?.scalingMode || 'cover',
      }}
    >
      {hasContent && (
        <Container
          className='block-container'
          sx={{
            paddingTop: '40px',
            paddingBottom: '40px',
            '&.MuiContainer-root': {
              paddingRight: {
                md: '80px',
              },
              paddingLeft: {
                md: '80px',
              },
            },
          }}
        >
          <BackgroundCard
            component='section'
            className={cardClass}
            background={cardBackgroundColor}
          >
            {title && (
              <Title variant='h3' data-epi-edit='Title' regular>
                {title}
              </Title>
            )}
            <Typography
              variant='body1'
              component='section'
              data-epi-edit='Text'
              className='description'
            >
              <Html html={text} />
            </Typography>
          </BackgroundCard>
        </Container>
      )}
    </Box>
  );

  if (hideOnMobile && isMobile) {
    return null;
  }

  return (
    <BannerBlockWrapper className={mainClass}>
      <>
        <EditPanel
          disableEditPanel={disableEditPanel}
          properties={['image', 'title', 'backgroundColor', 'cardBackgroundColor', 'link', 'text']}
        />
        {link && <SmartLink {...link}>{mainContent}</SmartLink>}
        {!link && mainContent}
      </>
    </BannerBlockWrapper>
  );
};
